/**
 * What editing a bill changes — used by the Review step (preview) and by the save (updateInvoice /
 * updatePurchase), so what the user sees is exactly what is written. Works for sales invoices
 * (stock goes out) and purchase bills (stock comes in).
 */
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

/** Receipts recorded against the bill (not voided). */
export function recordedReceipts(bill) {
  return (bill?.paymentHistory || []).filter((h) => !h.voided && Number(h.amount || 0) > 0);
}

const qtyByItem = (lines = []) => {
  const map = new Map();
  lines.forEach((li) => {
    if (!li.itemId || li.itemType === "service") return;
    const cur = map.get(li.itemId) || { itemName: li.itemName || "", qty: 0 };
    map.set(li.itemId, { itemName: cur.itemName || li.itemName || "", qty: round2(cur.qty + Number(li.qty || 0)) });
  });
  return map;
};

/**
 * old: the saved bill (fresh copy). next: { grandTotal, lineItems, <party id/name>, <date>, paidInFull }
 * paidInFull only matters when no payments are recorded (bill "Paid in Full" toggle).
 * opts: partyIdField / partyNameField / dateField, and stockSign: −1 sales (more qty → less stock), +1 purchases.
 */
export function diffBill(oldInv, next, { partyIdField = "customerId", partyNameField = "customerName", dateField = "invoiceDate", stockSign = -1 } = {}) {
  const receipts = recordedReceipts(oldInv);
  const recorded = round2(receipts.reduce((s, h) => s + Number(h.amount || 0), 0));
  const oldPaid = round2(oldInv.paid !== undefined ? oldInv.paid : (oldInv.paidAmount || 0));
  const oldTotal = round2(oldInv.grandTotal);
  const oldBalance = round2(oldInv.balance !== undefined ? oldInv.balance : Math.max(0, oldTotal - oldPaid));
  const newTotal = round2(next.grandTotal);

  // Paid amount after the edit: receipts never change here; a cash bill "Paid in Full" follows the new total
  let newPaid = oldPaid;
  if (!receipts.length) {
    const wasPaidInFull = oldPaid >= oldTotal - 0.005 && oldTotal > 0;
    if (next.paidInFull) newPaid = newTotal;
    else newPaid = wasPaidInFull ? 0 : oldPaid;
  }
  const overReceived = newPaid > newTotal + 0.005 ? round2(newPaid - newTotal) : 0;
  const newBalance = round2(Math.max(0, newTotal - newPaid));

  // Stock: positive delta = more sold (take from stock), negative = back to stock
  const before = qtyByItem(oldInv.lineItems);
  const after = qtyByItem(next.lineItems);
  const stock = [];
  new Set([...before.keys(), ...after.keys()]).forEach((itemId) => {
    const o = before.get(itemId)?.qty || 0;
    const n = after.get(itemId)?.qty || 0;
    const delta = round2(n - o);
    // stockChange: what happens to the item's stock (+ comes in, − goes out)
    if (Math.abs(delta) > 0.0001) stock.push({ itemId, itemName: after.get(itemId)?.itemName || before.get(itemId)?.itemName || "", oldQty: o, newQty: n, delta, stockChange: round2(stockSign * delta) });
  });

  // Customer dues (openingBalance) changes
  const customerChanged = (oldInv[partyIdField] || "") !== (next[partyIdField] || "");
  const dues = customerChanged
    ? [
        ...(oldInv[partyIdField] && oldBalance ? [{ customerId: oldInv[partyIdField], name: oldInv[partyNameField], delta: -oldBalance }] : []),
        ...(next[partyIdField] && newBalance ? [{ customerId: next[partyIdField], name: next[partyNameField], delta: newBalance }] : [])
      ]
    : (next[partyIdField] && Math.abs(newBalance - oldBalance) > 0.005 ? [{ customerId: next[partyIdField], name: next[partyNameField], delta: round2(newBalance - oldBalance) }] : []);

  const summary = [];
  if (Math.abs(newTotal - oldTotal) > 0.005) summary.push(`Total ₹${oldTotal.toLocaleString("en-IN")} → ₹${newTotal.toLocaleString("en-IN")}`);
  if (customerChanged) summary.push(`${stockSign > 0 ? "Vendor" : "Customer"} ${oldInv[partyNameField] || "—"} → ${next[partyNameField] || "—"}`);
  if (next[dateField] && next[dateField] !== oldInv[dateField]) summary.push(`Date ${oldInv[dateField]} → ${next[dateField]}`);
  stock.forEach((s) => summary.push(s.oldQty === 0 ? `Added ${s.itemName} × ${s.newQty}` : s.newQty === 0 ? `Removed ${s.itemName}` : `${s.itemName} qty ${s.oldQty} → ${s.newQty}`));
  if (!summary.length) summary.push("Details updated");

  return {
    receipts,
    recorded,
    oldTotal,
    newTotal,
    oldPaid,
    newPaid,
    oldBalance,
    newBalance,
    overReceived,
    customerChanged,
    customerLocked: receipts.length > 0,
    paymentStatus: newBalance <= 0.005 ? "Paid" : newPaid > 0 ? "Partial" : "Unpaid",
    stock,
    dues,
    summary
  };
}

export const diffInvoice = (oldInv, next) => diffBill(oldInv, next);
export const diffPurchase = (oldBill, next) => diffBill(oldBill, next, { partyIdField: "vendorId", partyNameField: "vendorName", dateField: "billDate", stockSign: 1 });
